import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import {
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    InputAdornment,
    Popover,
    TextField,
} from '@material-ui/core';
import CalendarToday from '@material-ui/icons/CalendarTodayTwoTone';
import DateFnsUtils from '@date-io/date-fns';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import classNames from 'classnames';
import format from 'date-fns/format';
import svLocale from 'date-fns/locale/sv';
import isSameDay from 'date-fns/isSameDay';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import isWithinInterval from 'date-fns/isWithinInterval';
import differenceInDays from 'date-fns/differenceInDays';
import translationPropType from '../../../proptypes/translationPropType';
import getStyles from './styles';
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';

const styles = theme => getStyles(theme);

const DateRangeSelect = ({
                             classes,
                             from,
                             to,
                             setValue,
                             locale,
                             translation,
                             disabled,
                         }) => {
    const getLocale = () => {
        switch (locale) {
            case 'sv-se':
                return svLocale;
            default:
                return svLocale;
        }
    };

    const [anchorEl, setAnchorEl] = useState(null);

    const handleOpen = (event) => {
        event.preventDefault();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // const handleSetYesterday = async () => {
    //     const now = new Date();
    //     const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
    //     const yesterday = new Date(today.setDate(today.getDate() - 1));
    //     await setValue('to', yesterday.toISOString()
    //         .split('T')[0]);
    //     setValue('from', yesterday.toISOString()
    //         .split('T')[0]);
    //     handleClose();
    // };

    const handleSetLastMonth = async () => {
        const now = new Date();
        const lastDayOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 1, -12);
        await setValue('to', lastDayOfLastMonth.toISOString()
            .split('T')[0]);
        const firstDayOfLastMonth = new Date(lastDayOfLastMonth.setDate(1));
        setValue('from', firstDayOfLastMonth.toISOString()
            .split('T')[0]);
        handleClose();
    };

    const handleSetPastYear = async () => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
        const today2 = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
        const yesterday = new Date(today.setDate(today.getDate() - 1));
        await setValue('to', yesterday.toISOString()
            .split('T')[0]);
        const todayOneYearAgo = new Date(today2.setFullYear(today2.getFullYear() - 1));
        setValue('from', todayOneYearAgo.toISOString()
            .split('T')[0]);
        handleClose();
    };

    const handleSetPastTwoYears = async () => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
        const today2 = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
        const yesterday = new Date(today.setDate(today.getDate() - 1));
        await setValue('to', yesterday.toISOString()
            .split('T')[0]);
        const todayOneYearAgo = new Date(today2.setFullYear(today2.getFullYear() - 2));
        setValue('from', todayOneYearAgo.toISOString()
            .split('T')[0]);
        handleClose();
    };

    const getYesterday = (asISOString) => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
        const yesterday = new Date(today.setDate(today.getDate() - 1));
        return asISOString ? yesterday.toISOString()
            .split('T')[0] : yesterday;
    };

    const getLastDayOfCurrentMonth = (asISOString) => {
        const now = new Date();
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0, 12); // 0 gives last day of current month
        return asISOString
            ? lastDay.toISOString()
                .split('T')[0]
            : lastDay;
    };

    const getStatisticsBegining = (asISOString) => {
        const start = new Date(2018, 0, 1, 0);
        return asISOString ? start.toISOString()
            .split('T')[0] : start;
    };

    function Alert(props) {
        return <MuiAlert elevation={6} variant="filled" {...props} />;
    }

    const handleSetTo = async (date) => {
        // Material-UI sets the date immediately when a month is picked
        if (!date) return;

        const fromDate = new Date(from).setHours(12);
        const lastOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0, 12);

        const daysBetween = Math.abs(differenceInDays(lastOfMonth, fromDate));
        if (daysBetween > 730) {
            setSnackbarOpen(true);
            return;
        }

        if (isBefore(lastOfMonth, fromDate)) {
            const firstOfMonth = new Date(lastOfMonth.getFullYear(), lastOfMonth.getMonth(), 1, 12);
            await setValue('from', firstOfMonth.toISOString()
                .split('T')[0]);
            await setValue('to', lastOfMonth.toISOString()
                .split('T')[0]);
        } else {
            setValue('to', lastOfMonth.toISOString()
                .split('T')[0]);
        }
    };

    const handleSetFrom = async (date) => {
        const toDate = new Date(to).setHours(12);
        const firstOfMonth = new Date(date.getFullYear(), date.getMonth(), 1, 12);

        const daysBetween = Math.abs(differenceInDays(toDate, firstOfMonth));
        if (daysBetween > 730) {
            setSnackbarOpen(true);
            return;
        }

        if (isAfter(firstOfMonth, toDate)) {
            const lastOfMonth = new Date(firstOfMonth.getFullYear(), firstOfMonth.getMonth() + 1, 0, 12);
            await setValue('from', firstOfMonth.toISOString()
                .split('T')[0]);
            await setValue('to', lastOfMonth.toISOString()
                .split('T')[0]);
        } else {
            setValue('from', firstOfMonth.toISOString()
                .split('T')[0]);
        }
    };

    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };

    const renderFromDay = (date, selectedDate, dayInCurrentMonth) => {
        const end = new Date(to).setHours(12);
        const start = new Date(from).setHours(12);

        const dayIsBetween = isWithinInterval(date, {
            start,
            end
        });
        const isFirstDay = isSameDay(date, start);
        const isLastDay = isSameDay(date, end);
        const isSelected = isSameDay(date, selectedDate);
        const isDisabled = isAfter(date, getYesterday()) || isBefore(date, getStatisticsBegining());

        const wrapperClassName = classNames({
            [classes.dayWrapper]: true,
            [classes.highlight]: dayIsBetween && dayInCurrentMonth,
            [classes.firstHighlight]: isFirstDay && dayInCurrentMonth,
            [classes.firstHighlightSelected]: isFirstDay && dayInCurrentMonth && isSelected,
            [classes.endHighlight]: isLastDay && dayInCurrentMonth,
            [classes.highlightFirstAndLast]: isLastDay
                && isFirstDay
                && dayInCurrentMonth
                && isSelected,
        });

        const dayClassName = classNames(classes.day, {
            [classes.selectedDay]: isSelected,
            [classes.lastDay]: isLastDay,
            [classes.nonCurrentMonthDay]: !dayInCurrentMonth,
            [classes.highlightNonCurrentMonthDay]: !dayInCurrentMonth && dayIsBetween,
        });

        return (
            <div className={wrapperClassName}>
                <IconButton className={dayClassName} disabled={isDisabled}>
                    {format(date, 'd')}
                </IconButton>
            </div>
        );
    };

    const renderToDay = (date, selectedDate, dayInCurrentMonth) => {
        const start = new Date(from).setHours(12);
        const end = new Date(to).setHours(12);

        const dayIsBetween = isWithinInterval(date, {
            start,
            end
        });
        const isFirstDay = isSameDay(date, start);
        const isLastDay = isSameDay(date, end);
        const isSelected = isSameDay(date, selectedDate);
        const isDisabled = isBefore(date, getStatisticsBegining()) || isAfter(date, getYesterday());

        const wrapperClassName = classNames({
            [classes.dayWrapper]: true,
            [classes.highlight]: dayIsBetween && dayInCurrentMonth,
            [classes.firstHighlight]: isFirstDay && dayInCurrentMonth,
            [classes.endHighlight]: isLastDay && dayInCurrentMonth,
            [classes.highlightFirstAndLast]: isLastDay
                && isFirstDay
                && dayInCurrentMonth
                && isSelected,
        });

        const dayClassName = classNames(classes.day, {
            [classes.selectedDay]: isSelected,
            [classes.firstDay]: isFirstDay,
            [classes.nonCurrentMonthDay]: !dayInCurrentMonth,
            [classes.highlightNonCurrentMonthDay]: !dayInCurrentMonth && dayIsBetween,
        });

        return (
            <div className={wrapperClassName}>
                <IconButton className={dayClassName} disabled={isDisabled}>
                    {format(date, 'd')}
                </IconButton>
            </div>
        );
    };

    const open = Boolean(anchorEl);

    const quickChoices = [
        // {
        //     onClick: handleSetYesterday,
        //     text: translation.YESTERDAY,
        // },
        // {
        //     onClick: handleSetLastWeek,
        //     text: translation.LAST_WEEK,
        // },
        {
            onClick: handleSetLastMonth,
            text: translation.LAST_MONTH,
        },
        {
            onClick: handleSetPastYear,
            text: translation.PAST_YEAR,
        },
        {
            onClick: handleSetPastTwoYears,
            text: translation.PAST_TWO_YEARS,
        },
    ];

    const renderPicker = (label, value, renderDay, onChange) => (
        <DatePicker
            variant="inline"
            inputVariant="outlined"
            disableToolbar
            label={label}
            value={value}
            views={['year', 'month']}
            minDate={getStatisticsBegining(true)}
            maxDate={getLastDayOfCurrentMonth()}
            renderDay={renderDay}
            onChange={onChange}
            format="yyyy-MM-dd"
            fullWidth
            autoOk
        />
    );

    return (
        <div>
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={getLocale()}>
                <TextField
                    aria-owns={open ? 'simple-popper' : undefined}
                    aria-haspopup="true"
                    label={translation.PERIOD}
                    type="text"
                    variant="outlined"
                    margin="dense"
                    value={`${from} - ${to}`}
                    fullWidth
                    onClick={disabled ? null : handleOpen}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    InputProps={{
                        readOnly: true,
                        disabled,
                        endAdornment: (
                            <InputAdornment>
                                <CalendarToday color={disabled ? 'disabled' : 'primary'}/>
                            </InputAdornment>
                        ),
                    }}
                />
                <Popover
                    id="simple-popper"
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                >
                    <Card className={classes.card}>
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    {renderPicker(translation.FROM, from, renderFromDay, handleSetFrom)}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {renderPicker(translation.TO, to, renderToDay, handleSetTo)}
                                </Grid>
                                <Grid item xs={12}>
                                    {quickChoices.map(choise => (
                                        <Button
                                            className={classes.button}
                                            color="default"
                                            variant="contained"
                                            size="small"
                                            margin="dense"
                                            onClick={choise.onClick}
                                            key={choise.text}
                                        >
                                            {choise.text}
                                        </Button>
                                    ))}
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Popover>
                <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={handleSnackbarClose}>
                    <Alert severity="warning" onClose={handleSnackbarClose}>
                        {translation.ERROR_MESSAGE_DATE_RANGE}
                    </Alert>
                </Snackbar>
            </MuiPickersUtilsProvider>
        </div>
    );
};

DateRangeSelect.propTypes = {
    classes: PropTypes.objectOf(PropTypes.any).isRequired,
    translation: translationPropType.isRequired,
    from: PropTypes.string.isRequired,
    to: PropTypes.string.isRequired,
    setValue: PropTypes.func.isRequired,
    locale: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
};

DateRangeSelect.defaultProps = {
    disabled: false,
};


export default withStyles(styles)(DateRangeSelect);
